import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import SectionTitle from '../ui/SectionTitle';
import GoldButton from '../ui/GoldButton';
import MapsEmbed from '../ui/MapsEmbed';
import PaletteCircle from '../ui/PaletteCircle';
import ImageWithFallback from '../ui/ImageWithFallback';

const RingIcon = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="24" cy="30" r="12" />
    <path d="M18 18 L24 12 L30 18" strokeLinejoin="round" strokeLinecap="round" />
    <circle cx="24" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

const GlassIcon = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M14 8 L34 8 L31 22 Q24 30 17 22 Z" strokeLinejoin="round" />
    <path d="M24 30 L24 40" strokeLinecap="round" />
    <path d="M18 42 L30 42" strokeLinecap="round" />
  </svg>
);

interface RowProps {
  Icon: React.ComponentType;
  label: string;
  children: React.ReactNode;
}

function Row({ Icon, label, children }: RowProps) {
  return (
    <div className="grid grid-cols-[48px_1fr] gap-4 items-start py-4 border-b border-brand-gold/15 last:border-0">
      <div className="text-brand-gold flex justify-center pt-1">
        <Icon />
      </div>
      <div>
        <p className="font-sans text-[0.7rem] tracking-widest uppercase text-brand-gold">
          {label}
        </p>
        <div className="mt-1 font-serif text-brand-ink leading-snug text-[0.95rem] sm:text-base">
          {children}
        </div>
      </div>
    </div>
  );
}

const PALETTE: Array<{ key: string; bg: string }> = [
  { key: 'mauve', bg: 'bg-brand-mauve' },
  { key: 'cream', bg: 'bg-brand-cream' },
  { key: 'terracotta', bg: 'bg-brand-terracotta' },
  { key: 'sage', bg: 'bg-brand-sage' },
  { key: 'olive', bg: 'bg-brand-olive' },
  { key: 'rose', bg: 'bg-brand-rose' },
];

function DressCodeCoupleFallback() {
  return (
    <svg viewBox="0 0 220 360" className="w-full max-w-[220px] mx-auto" aria-hidden="true">
      <g transform="translate(15, 60)">
        <ellipse cx="40" cy="24" rx="16" ry="20" fill="#D9B895" />
        <path d="M28,10 Q40,-2 52,10 Q54,28 48,30 L32,30 Q26,28 28,10 Z" fill="#7A5C3C" />
        <path d="M16,60 L64,60 L72,180 L8,180 Z" fill="#7A7E83" />
        <path d="M16,180 L36,180 L34,290 L18,290 Z" fill="#5A5E62" />
        <path d="M44,180 L64,180 L62,290 L46,290 Z" fill="#5A5E62" />
      </g>
      <g transform="translate(115, 60)">
        <ellipse cx="40" cy="24" rx="16" ry="20" fill="#EFE3D2" />
        <path d="M28,4 Q40,-8 52,4 Q56,22 50,32 L32,32 Q24,28 28,4 Z" fill="#8A6B47" />
        <path d="M22,60 L58,60 L66,110 L80,290 L0,290 L14,110 Z" fill="#9FAE8E" />
      </g>
    </svg>
  );
}

export default function DetailsAndDressCode() {
  const { t } = useTranslation();
  return (
    <section
      id="details"
      className="relative bg-brand-paper-warm/60 border-y border-brand-gold/20 overflow-hidden"
      aria-label="Details and Dress Code"
    >
      {/* Subtle decorations — JPGs with white backgrounds use mix-blend-multiply */}
      <img
        src="/images/dress-code-butterfly.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute top-6 left-[18%] w-20 sm:w-24 opacity-90"
        style={{ mixBlendMode: 'multiply' }}
      />
      <img
        src="/images/dress-code-butterfly.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute bottom-8 right-[22%] w-16 sm:w-20 opacity-85"
        style={{ mixBlendMode: 'multiply', transform: 'scaleX(-1)' }}
      />
      <img
        src="/images/dress-code-hummingbird.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute top-1/3 left-2 w-20 sm:w-24 opacity-80"
        style={{ mixBlendMode: 'multiply' }}
      />
      <img
        src="/images/dress-code-hummingbird.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute bottom-1/3 right-2 w-20 sm:w-24 opacity-80"
        style={{ mixBlendMode: 'multiply', transform: 'scaleX(-1)' }}
      />

      <div
        id="location"
        className="relative mx-auto max-w-5xl px-6 py-16 sm:py-20 flex flex-col gap-12 sm:gap-16"
      >
        {/* Dress Code card */}
        <div className="bg-white rounded-2xl shadow-card p-8 sm:p-12 flex flex-col">
          <SectionTitle>{t('dressCode.title')}</SectionTitle>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_1.5fr] gap-8 items-center">
            <div>
              <ImageWithFallback
                src="/images/dress-code-couple.jpg"
                alt=""
                fallback={<DressCodeCoupleFallback />}
                className="w-full max-w-[260px] mx-auto h-auto object-contain"
              />
            </div>
            <div>
              <div className="space-y-6">
                <div>
                  <p className="font-sans text-[0.8rem] tracking-widest uppercase text-brand-gold">
                    {t('dressCode.ladiesLabel')}
                  </p>
                  <p className="font-serif italic text-brand-ink/90 mt-2 text-base sm:text-lg">
                    {t('dressCode.ladiesText')}
                  </p>
                </div>
                <div>
                  <p className="font-sans text-[0.8rem] tracking-widest uppercase text-brand-gold">
                    {t('dressCode.gentlemenLabel')}
                  </p>
                  <p className="font-serif italic text-brand-ink/90 mt-2 text-base sm:text-lg">
                    {t('dressCode.gentlemenText')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-brand-gold/20 text-center">
            <p className="font-sans text-[0.8rem] tracking-widest uppercase text-brand-ink/80 mt-1">
              {t('dressCode.rule2')}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              {PALETTE.map((p) => (
                <PaletteCircle
                  key={p.key}
                  bgClass={p.bg}
                  label={t(`dressCode.palette.${p.key}`)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Details card — spotless white */}
        <div className="bg-white rounded-2xl shadow-card p-8 sm:p-12 flex flex-col">
          <SectionTitle>{t('details.title')}</SectionTitle>

          <div className="mt-8">
            <Row Icon={RingIcon} label={t('details.ceremony')}>
              {t('details.ceremonyTime')}
            </Row>
            <Row Icon={GlassIcon} label={t('details.reception')}>
              {t('details.receptionTime')}
            </Row>
            <Row Icon={MapPin} label={t('details.location')}>
              <p>{t('details.venueName')}</p>
              <p>{t('details.venueStreet')}</p>
              <p>{t('details.venueCity')}</p>
            </Row>
          </div>

          <div className="mt-8">
            <MapsEmbed />
          </div>

          <div className="mt-6 flex justify-center">
            <GoldButton
              as="a"
              href="https://www.google.com/maps/search/?api=1&query=Hyatt+Regency+Lake+Tahoe+Resort,+Incline+Village,+NV"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('details.openInMaps')}
            </GoldButton>
          </div>
        </div>
      </div>
    </section>
  );
}
